import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { takeUntil, catchError, take, debounceTime } from 'rxjs/operators';

import { SearchTypeEnum } from '@enum/search.enum';

import { ToastService } from '@service/toast.service';
import { SearchService } from '@service/search.service';
import { ValidationService } from '@service/validation.service';
import { InsuredPersonService } from '@service/insured-person.service';
import { MasterDataService } from '@service/master-data.service';

import { DEFAULT_MIN_LENGTH, DEFAULT_MAX_LENGTH } from '@constant/search.constant';

import { Party } from '@model/party.model';
import { SearchResult } from '@model/search.model';
import { Consent } from '@model/master-data.model';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchComponent implements OnInit, OnDestroy, OnChanges {
  @Input() id: string;
  @Input() key: number;

  @Input() searchBtnText: string = 'searchButton';
  @Input() searchInputPlaceholder: string = 'searchPlaceholder';

  @Input() searchType: SearchTypeEnum.NATIONAL_ID | SearchTypeEnum.PASSPORT_NO | SearchTypeEnum.REGISTRATION_NO;

  @Input() minLength: number = DEFAULT_MIN_LENGTH;
  @Input() maxLength: number = DEFAULT_MAX_LENGTH;

  @Input() hasDupclicatePartyError: boolean = false;
  @Input() shouldCheckLocalDuplicate: boolean = false;

  @Output() selectResult: EventEmitter<SearchResult> = new EventEmitter();

  @ViewChild('searchContainer', { static: true }) searchContainer: ElementRef;

  isLoading: boolean = false;
  isNumberOnly: boolean = false;
  showDropdown: boolean = false;
  showPersonTemplate: boolean;
  wrongNationalIdFormat: boolean = false;
  hasLocallyDupclicatePartyError: boolean = false;

  searchResults: Party[];
  searchKeywords: UntypedFormControl = new UntypedFormControl('');
  private consentIds: string[] = [];

  private validators: ValidatorFn[] = [];

  private selectedResult: any = null;
  private didPressSearchBtn: boolean = false;

  private DEBOUNCE_TIME: number = 500;

  private onDestroy$: Subject<void> = new Subject();
  private onSearchClick$: Subject<void> = new Subject();

  disableSubmitButton: boolean = true;

  constructor(
    private toastService: ToastService,
    private searchService: SearchService,
    private changeDetector: ChangeDetectorRef,
    private validationService: ValidationService,
    private insuredPersonService: InsuredPersonService,
    private masterDataService: MasterDataService
  ) {}

  ngOnInit(): void {
    this.addOnClickListener();
    this.initConsentIds();
    this.setOptionTemplate();
    this.setFormValidators();

    this.listenToInputChange();
    this.updateInputConstraint();
  }

  initConsentIds(): void {
    this.masterDataService
      .getConsent()
      .pipe(take(1))
      .subscribe((consents: Consent[]) => {
        this.consentIds = consents.map((consent: Consent) => consent.code);
      });
  }

  ngOnDestroy(): void {
    this.onDestroy();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.searchType && changes.searchType.currentValue !== changes.searchType.previousValue) {
      this.updateInputConstraint();
      this.resetFormValidationState();
    }
  }

  addOnClickListener(): void {
    this.onSearchClick$.pipe(debounceTime(this.DEBOUNCE_TIME)).subscribe(() => {
      this.searchValidation();
    });
  }

  onSearchClick(): void {
    this.isLoading = true;
    this.onSearchClick$.next();
  }

  searchValidation(): void {
    this.didPressSearchBtn = true;

    const hasCorrectFormat: boolean = this.checkNationalIdFormat();
    const hasNoDuplicated: boolean = !this.checkPartyDuplicationLocally();

    if (hasNoDuplicated) {
      if (hasCorrectFormat) {
        this.searchQuery();
      } else {
        this.isLoading = false;
        this.wrongNationalIdFormat = true;
      }
    } else {
      this.isLoading = false;
    }
    this.changeDetector.detectChanges();
  }

  searchQuery(): void {
    this.searchService
      .search(this.searchKeywords.value, this.searchType, this.consentIds)
      .pipe(
        take(1),
        catchError(() => {
          this.isLoading = false;
          return of([]);
        })
      )
      .subscribe((results: Party[]) => {
        this.searchResults = results;
        this.showDropdown = results.length > 1;

        switch (this.searchResults.length) {
          case 0:
            this.selectResult.emit({ index: this.key, searchResult: null, searchInput: this.searchKeywords.value });
            break;
          case 1:
            this.onSelect(results[0]);
            break;
          default:
            break;
        }
        this.isLoading = false;
        this.changeDetector.detectChanges();
      });
  }

  onSelect(selectedItem: any): void {
    if (selectedItem) {
      this.selectedResult = selectedItem;

      const errors: string[] = this.searchService.validateSearchResult(selectedItem);
      const searchResultValid: boolean = errors.length === 0;

      if (searchResultValid) {
        this.selectResult.emit({ index: this.key, searchResult: this.selectedResult, searchInput: null });
      } else {
        this.toastService.show('invalidSearchDataError', errors, false);
      }

      this.closeDropdown();
    }
  }

  resetSearchKeywordInput(): void {
    this.searchKeywords.reset();
  }

  private clearDuplicateError(): void {
    this.hasDupclicatePartyError = false;
    this.hasLocallyDupclicatePartyError = false;
  }

  private checkNationalIdFormat(): boolean {
    if (this.searchType !== SearchTypeEnum.NATIONAL_ID) {
      return true;
    }

    return this.validationService.nationalId(this.searchKeywords.value);
  }

  private checkPartyDuplicationLocally(): boolean {
    if (!this.shouldCheckLocalDuplicate) {
      return false;
    }

    this.insuredPersonService
      .checkPartyDuplication(
        this.key,
        this.searchType === SearchTypeEnum.NATIONAL_ID ? this.searchKeywords.value : null,
        this.searchType === SearchTypeEnum.PASSPORT_NO ? this.searchKeywords.value : null
      )
      .pipe(take(1))
      .subscribe((hasDupclicateParty: boolean) => {
        this.hasLocallyDupclicatePartyError = hasDupclicateParty;
      });
    return this.hasLocallyDupclicatePartyError;
  }

  private onDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private resetFormValidationState(): void {
    this.searchKeywords.markAsPristine();
    this.searchKeywords.markAsUntouched();
  }

  private updateInputConstraint(): void {
    const isSearchByNationalID: boolean = this.searchType === SearchTypeEnum.NATIONAL_ID;
    const isSearchByRegistrationNo: boolean = this.searchType === SearchTypeEnum.REGISTRATION_NO;

    this.minLength = isSearchByNationalID || isSearchByRegistrationNo ? 13 : DEFAULT_MIN_LENGTH;
    this.maxLength = isSearchByNationalID || isSearchByRegistrationNo ? 13 : DEFAULT_MAX_LENGTH;

    this.isNumberOnly = isSearchByNationalID || isSearchByRegistrationNo;

    this.clearSearchInput();
    this.clearDuplicateError();

    this.setFormValidators();
    this.searchKeywords.updateValueAndValidity();
  }

  private clearSearchInput(): void {
    this.searchKeywords.setValue(null);
  }

  private setOptionTemplate(): void {
    this.showPersonTemplate = this.searchType === SearchTypeEnum.NATIONAL_ID || this.searchType === SearchTypeEnum.PASSPORT_NO;
  }

  private setFormValidators(): void {
    this.validators = [Validators.required, Validators.minLength(this.minLength), Validators.maxLength(this.maxLength)];

    this.searchKeywords.setValidators(this.validators);
    this.searchKeywords.updateValueAndValidity();
  }

  private closeDropdown(): void {
    this.showDropdown = false;
  }

  private listenToInputChange(): void {
    this.searchKeywords.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      const shouldHaveMinLength: boolean =
        this.searchType === SearchTypeEnum.NATIONAL_ID || this.searchType === SearchTypeEnum.REGISTRATION_NO;
      const didReachRequiredLength: boolean = shouldHaveMinLength
        ? this.searchKeywords.value && this.searchKeywords.value.length === 13
        : true;

      if (this.searchKeywords.valid && didReachRequiredLength) {
        this.disableSubmitButton = false;
      } else {
        this.disableSubmitButton = true;
      }

      if (this.didPressSearchBtn) {
        this.closeDropdown();
        this.clearDuplicateError();

        this.wrongNationalIdFormat = false;
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onOutsideClick(event: Event): void {
    if (!this.searchContainer.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }
}
